import {
  DEFAULT_BORDER_RADIUS,
  DEFAULT_BORDER_SIZE,
  DEFAULT_FONT_SIZE,
  DEFAULT_LETTER_SPACING,
  DEFAULT_PADDING,
  PREDEFINED_TAG_COLORS,
  TAG_COLORS,
  TagColorMode,
  TagVariant
} from '@treneo/shared/constants';
import { Colors } from '@treneo/shared/themes';
import { styled } from '@treneo/shared/ui';

const getDarkerColor = (color: string): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkerR = Math.max(0, Math.floor(r * 0.7));
  const darkerG = Math.max(0, Math.floor(g * 0.7));
  const darkerB = Math.max(0, Math.floor(b * 0.7));

  return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
};

export const CourseChip = styled('span')<{
  backgroundColor: string;
  variant?: TagVariant;
  uniformColor?: string;
  borderSize?: number;
  fontSize?: number;
  colorMode?: TagColorMode;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${DEFAULT_PADDING};
  border-radius: ${DEFAULT_BORDER_RADIUS}px;
  font-size: ${({ fontSize = DEFAULT_FONT_SIZE }) => fontSize}px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: ${DEFAULT_LETTER_SPACING}px;
  cursor: default;
  pointer-events: none;
  border: ${({ borderSize = DEFAULT_BORDER_SIZE }) => borderSize}px solid transparent;

  ${({ backgroundColor, variant = TagVariant.FILLED, uniformColor, colorMode }) => {
    const colorToUse =
      colorMode === TagColorMode.UNIFORM && uniformColor ? uniformColor : backgroundColor;

    if (variant === TagVariant.OUTLINED) {
      return `
        color: ${getDarkerColor(colorToUse)};
        background-color: ${getDarkerColor(colorToUse)}08;
        border-color: ${getDarkerColor(colorToUse)};
        font-weight: 600;
      `;
    }

    return `
      color: ${Colors.white};
      background-color: ${colorToUse};
    `;
  }}

  &:hover {
    transform: none;
    box-shadow: none;
    opacity: 1;
  }
`;

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return Math.abs(hash);
};

export const getTagColor = (
  tag: string | undefined | null,
  _variant: TagVariant = TagVariant.FILLED,
  uniformColor?: string,
  colorMode?: TagColorMode
): string => {
  if (colorMode === TagColorMode.UNIFORM && uniformColor) {
    return uniformColor;
  }

  if (!tag || typeof tag !== 'string') {
    return TAG_COLORS[TAG_COLORS.length - 1];
  }

  const lowerTag = tag.toLowerCase();
  if (PREDEFINED_TAG_COLORS[lowerTag]) {
    return PREDEFINED_TAG_COLORS[lowerTag];
  }

  const hash = hashString(tag);
  return TAG_COLORS[hash % TAG_COLORS.length];
};
